import React from "react";
import _ from "lodash";

import {makeStyles} from "@material-ui/core";
import Grid from "@material-ui/core/Grid/Grid";
import InputAdornment from "@material-ui/core/InputAdornment";

import InputFormElement, {CurrencyNumberFormat} from "../../../../../formElement/InputFormElement";
import WarningTooltip from "../../../../../../../../../../components/WarningTooltip";
import DashboardTable from "../../../../../../../../../../components/DashboardTable";
import {tableOngoingCostsStructure, useInputStyles} from "./table_data";
import {getVatCoef} from "../../../../../../../../utils";
import {PercentageNumberFormat} from "../../../../../formElement/InputFormElement/InputFormElement";
import {withEuroOrDash, withPercentOrDash} from "../../../../../../../../../../utils/utils";
import BaseNumberFormat from "../../../../../../../../../../components/Inputs/BaseNumberFormat/BaseNumberFormat";
import InfoCard from "../../../../DynamicStep/components/InfoCard";
import WarningIcon from "../../../../../../../../../../components/Icons/WarningIcon";
import {NO_RISK_PROFILE_MSG} from "../../../constants";

const useStyles = makeStyles((theme) => ({
  pageTitle: {
    fontSize: 24
  },
  table: {
    '& tbody tr td': {
      verticalAlign: 'middle',
      padding: '4px 8px',
      width: 140,
    },
    '& tbody tr td:first-child': {
      width: 'auto'
    }
  },
  header: {
    fontSize: 18,
    fontFamily: "Roboto-Bold"
  },
  depotCard: {
    border: '1px solid #D9DCDF',
    borderRadius: 16,
    padding: theme.spacing(2),
    fontSize: 16,

    '& img': {
      height: 72
    }
  },
  amountCard: {
    border: '1px solid #B5BBC2',
    padding: theme.spacing(1),
    borderRadius: 8,
  }
}))

const setFeeVat = (feeVatQ, feeValue, vatCoef) => {
  feeVatQ.answer = _.round(feeValue * vatCoef, 2) || 0
}

const setFeeDefaults = (q, qVat, feeConfig, onAnswerChange, vatCoef) => {
  q.maxValue = _.get(feeConfig, [q.uid, 'max'])

  if(q.answer === undefined){
    const defValue = _.get(feeConfig, [q.uid, 'default'])
    if(!_.isNil(defValue)){
      setFeeVat(qVat, defValue, vatCoef)

      onAnswerChange(q.uid, defValue)
    }
  }
}

const CostDefinitions = (props) => {
  const {
    questions,
    onAnswerChange,
    dataService
  } = props;

  const classes = useStyles();

  const depot = React.useMemo(() => {
    return dataService.getDepot();
  }, [dataService]);

  const missingDataInfos = [];
  if(!depot){
    missingDataInfos.push('Bitte wählen Sie zuerst eine Depotbank aus. Danach zeigen wir Ihnen die verfügbaren Optionen an.')
  }
  if(!dataService._srri){
    missingDataInfos.push(NO_RISK_PROFILE_MSG)
  }

  return (
    <Grid container spacing={4}>
      <Grid item xs={12} className={classes.pageTitle}>
        <b>Anlageplan und Gebühren</b>
      </Grid>
      {missingDataInfos.length ? (<>
        {missingDataInfos.map((msg, idx) => (
          <Grid key={idx} item xs={12}>
            <InfoCard isNewDesign cardType={'warning'} info_text={msg} />
          </Grid>
        ))}
      </>) : (
        <CostDefinitionPage
          classes={classes}
          questions={questions}
          onAnswerChange={onAnswerChange}
          dataService={dataService}
          depot={depot}
        />
      )}
    </Grid>
  )
}

export default CostDefinitions;

const CostDefinitionPage = (props) => {
  const {
    classes,
    questions: [
      investedAmountQ,
      ongoingGroupQ,
      ongoingCostConfigQ,
      avgTransactionAmountQ,
      numberOfTradesGroupQ,
      serviceFeeGroupQ,
    ],
    onAnswerChange,
    dataService,
    depot,
  } = props;

  const [vvFeeQ, vvFeeVatQ, entryFeeQ, entryFeeVatQ] = serviceFeeGroupQ.question;

  const agency = dataService.current_customer.agency;

  const assetManagementConfig = React.useMemo(() => {
    const config = dataService.getBankConfig(depot)['individual_asset_managment_config'] || {};
    const agencyConfig = _.get(config, ['agencies_overrides', agency]);
    if (agencyConfig){
      return _.mergeWith({}, config, agencyConfig, (objValue, srcValue) => {
        // If the value is an array, return the source value to overwrite it completely
        if (_.isArray(objValue)) {
          return srcValue;
        }
      });
    }
    return config;
  }, [dataService, agency, depot]);

  const feeConfig = assetManagementConfig.fee_config;

  const vatCoef = React.useMemo(getVatCoef, []);

  React.useEffect(() => {
    setFeeDefaults(vvFeeQ, vvFeeVatQ, feeConfig, onAnswerChange, vatCoef)
    setFeeDefaults(entryFeeQ, entryFeeVatQ, feeConfig, onAnswerChange, vatCoef)
  }, [])

  investedAmountQ.minValue = assetManagementConfig.min_order_amount;

  const investedAmount = investedAmountQ.answer;
  const sri = dataService._srri;
  const minWarnAmount = assetManagementConfig.min_order_warn_amount;

  React.useEffect(() => {
    let minValue = _.get(feeConfig, [vvFeeQ.uid, 'min'])

    if(investedAmount){
      // we assume rules already sorted by 'min_order_amount'
      const minConfig = _.get(feeConfig, [vvFeeQ.uid, 'min_rules'], [])
        .find(r => investedAmount >= r.min_order_amount)

      if(minConfig){
        minValue = minConfig.fee;
      }
    }

    vvFeeQ.minValue = minValue;
  }, [investedAmount])

  return (
    <>
      <Grid item xs={12}>
        <Grid container>
          <Grid item xs={12} className={classes.header}>Depotführungskosten</Grid>
          <Grid item xs={12} data-id={'depot-data-container'}>
            <div className={classes.depotCard}>
              <Grid container spacing={3} alignItems={'center'}>
                <Grid item xs={'auto'}><img alt={''} src={depot.logo} /></Grid>
                <Grid item xs>
                  <div style={{marginBottom: 8}}><b>Depotmodell</b></div>
                  <div>{
                    depot.selected_contract_type
                      ? depot.selected_contract_type.label
                      : <><WarningIcon /> Bitte wählen Sie die gewünschte Depotart aus</>
                  }
                  </div>
                </Grid>
                <Grid item xs>
                  <div style={{marginBottom: 8}}><b>Depotkosten</b></div>
                  <div>{withEuroOrDash(0)}</div>
                </Grid>
              </Grid>
            </div>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Grid container>
          <Grid item xs={12} className={classes.header}>Anlageplan</Grid>
          <Grid item xs={12}>
            <Grid container alignItems={"center"} spacing={2}>
              <Grid item xs={12} sm>
                <AmountField
                  question={investedAmountQ}
                  onAnswerChange={onAnswerChange}
                  label={<>
                    <b>Gesamtanlage</b>
                    {!!minWarnAmount && !!investedAmount && minWarnAmount > investedAmount && (
                      <WarningTooltip title={`Mindestanlagesumme: ${withEuroOrDash(minWarnAmount)}`} width={600}/>
                    )}
                  </>}
                />
              </Grid>
              <Grid item xs={12} sm>
                <AmountField
                  question={avgTransactionAmountQ}
                  onAnswerChange={onAnswerChange}
                />
              </Grid>
              {numberOfTradesGroupQ.question.map(q => (
                <Grid item xs={12} sm key={q.uid}>
                  <InputField
                    question={q}
                    onAnswerChange={onAnswerChange}
                    inputComponent={IntegerNumberFormat}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Grid container>
          <Grid item xs={12} className={classes.header}>Laufende Produktkosten</Grid>
          <Grid item xs={12} data-id={'ongoing-cost-table-container'}>
            <OngoingCostTable
              onAnswerChange={onAnswerChange}
              SRIConfig={assetManagementConfig.sri_config[sri]}
              ongoingGroupQ={ongoingGroupQ}
              ongoingCostConfigQ={ongoingCostConfigQ}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={1}>
          <Grid item xs={12} className={classes.header}>Dienstleistungskosten</Grid>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={'auto'}>
                <FeePercField feeQ={vvFeeQ} feeVatQ={vvFeeVatQ} onAnswerChange={onAnswerChange} vatCoef={vatCoef} />
              </Grid>
              <Grid item xs={12} sm={'auto'}>
                <FeePercField feeQ={entryFeeQ} feeVatQ={entryFeeVatQ} onAnswerChange={onAnswerChange} vatCoef={vatCoef} />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  )
}

const OngoingCostTable = ({onAnswerChange, ongoingGroupQ, ongoingCostConfigQ, SRIConfig}) => {
  const tableClasses = useStyles();

  const costsConfig = SRIConfig.ongoing_costs;

  React.useEffect(() => {
    onAnswerChange(ongoingCostConfigQ.uid, costsConfig)
  }, [costsConfig])

  return (
    <DashboardTable
      structure={tableOngoingCostsStructure}
      dataSource={ongoingGroupQ.question}
      expanded
      tableLayout={'auto'}
      tableClasses={tableClasses}
      withFooter
      withInputs
      options={{
        onChange: (item, value) => {
          ongoingGroupQ.error = null;
          onAnswerChange(item.uid, value || 0)
        },
        costsConfig: costsConfig,
        maxValues: SRIConfig.ongoing_costs_max_values,
        costError: ongoingGroupQ.error
      }}
    />
  )
}

const IntegerNumberFormat = (props) => <BaseNumberFormat {...props} decimalScale={0} />

const AmountField = ({question, onAnswerChange, label}) => (
  <InputField
    question={question}
    label={label}
    onAnswerChange={onAnswerChange}
    inputComponent={CurrencyNumberFormat}
  />
)

const FeePercField = ({feeQ, feeVatQ, onAnswerChange, vatCoef}) => (
  <InputField
    question={feeQ}
    inputComponent={PercentageNumberFormat}
    onAnswerChange={(uid, value) => {
      setFeeVat(feeVatQ, value, vatCoef)

      onAnswerChange(uid, value)
    }}
    inputProps={{
      endAdornment: (
        <InputAdornment position="end">
        (zzgl. MwSt.) |&nbsp;<b>{withPercentOrDash(feeVatQ.answer || 0, false)} (inkl. MwSt.)</b>
      </InputAdornment>
      )}
    }
  />
)

const InputField = ({question, onAnswerChange, inputComponent, label, inputProps}) => {
  const classes = useInputStyles();

  return (
    <InputFormElement
      value={question.answer}
      required
      onChange={(value) => {
        question.error = null;
        onAnswerChange(question.uid, value)
      }}
      label={label || <b>{question.question_text}{!!question.info_text && <WarningTooltip title={question.info_text} width={600}/>}</b>}
      inputComponent={inputComponent}
      inputProps={{inputProps: {allowNegative: false, fixedDecimalScale: true}, ...inputProps}}
      custom_classes={classes}
      error={question.error}
      helperText={' '} // to keeps the height allocated
      dataId={question.uid}
    />
  )
}

